import type { ClockPort, TimerHandle } from '@lw/shared/clock';
import {
  combineIndicator,
  counterpartExpected,
  describeIndicator,
  initialConnTrack,
  initialSyncTrack,
  stepConnection,
  stepSync,
  type ConnTrack,
  type IndicatorLabel,
  type IndicatorState,
  type MissionState,
  type PairView,
  type SyncTrack,
} from '@lw/shared/indicator';
import type { ChronoClient } from './ChronoClient.js';

export interface IndicatorTransition {
  type: 'indicator.transition';
  from: IndicatorState['colour'] | null;
  to: IndicatorState['colour'];
  reasons: string[];
  /** client's server-time estimate at the transition, and its uncertainty */
  at: number | null;
  atUncertainty: number | null;
}

export interface IndicatorInputs {
  role: 'UAV' | 'FDC' | 'OBSERVER' | 'ADMIN';
  wsOpen: () => boolean;
  pollAgeMs?: () => number | null;
  navigatorOnline?: () => boolean;
  reconnects300?: () => number;
  mission: () => { state: MissionState; seats: { UAV: boolean; FDC: boolean } };
  pair: () => PairView | null;
  onTransition?: (t: IndicatorTransition) => void;
}

/** Drives the pure indicator functions every 250 ms from the kernel's measurements (§7.1). */
export class IndicatorController {
  private conn: ConnTrack = initialConnTrack;
  private sync: SyncTrack = initialSyncTrack;
  private state: IndicatorState | null = null;
  private handle: TimerHandle | null = null;
  private lostSincePerf: number | null = null;

  constructor(
    private readonly clock: ClockPort,
    private readonly chrono: ChronoClient,
    private readonly inputs: IndicatorInputs,
    private readonly tickMs = 250,
  ) {}

  start(): void {
    this.evaluate();
    const loop = (): void => {
      this.evaluate();
      this.handle = this.clock.setTimeout(loop, this.tickMs);
    };
    this.handle = this.clock.setTimeout(loop, this.tickMs);
  }

  stop(): void {
    if (this.handle) this.clock.clearTimeout(this.handle);
    this.handle = null;
  }

  current(): IndicatorState {
    return this.state ?? { colour: 'RED', reasons: ['CONN_CLOSED'] };
  }

  connTrack(): ConnTrack {
    return this.conn;
  }

  syncTrack(): SyncTrack {
    return this.sync;
  }

  label(): IndicatorLabel {
    const pair = this.inputs.pair();
    const expected = this.expectsCounterpart();
    const shown = expected && pair?.known && pair.bound !== null ? pair.bound : this.chrono.uncertainty();
    const offlineFor = this.lostSincePerf === null ? null : this.clock.perfNow() - this.lostSincePerf;
    return describeIndicator(this.current(), this.sync, shown, offlineFor);
  }

  /** One evaluation; also callable on every inbound frame while hidden. */
  evaluate(): IndicatorState {
    const now = this.clock.perfNow();
    const m = this.chrono.metrics();
    this.conn = stepConnection(
      this.conn,
      {
        wsOpen: this.inputs.wsOpen(),
        pollAgeMs: this.inputs.pollAgeMs?.() ?? null,
        sincePongMs: m.sincePongMs,
        navigatorOnline: this.inputs.navigatorOnline?.() ?? true,
        lost30: m.lost30,
        rttMin30: m.rttMin30,
        rttP90_30: m.rttP90_30,
        reconnects300: this.inputs.reconnects300?.() ?? 0,
      },
      now,
    );
    this.sync = stepSync(
      this.sync,
      {
        uSelf: this.chrono.uncertainty(),
        samplesSinceInvalidation: this.chrono.samplesAccepted(),
        counterpartExpected: this.expectsCounterpart(),
        pair: this.inputs.pair(),
      },
      now,
    );
    const next = combineIndicator(this.conn, this.sync);
    if (next.colour === 'RED') this.lostSincePerf ??= now;
    else this.lostSincePerf = null;
    if (!this.state || this.state.colour !== next.colour) {
      this.inputs.onTransition?.({
        type: 'indicator.transition',
        from: this.state?.colour ?? null,
        to: next.colour,
        reasons: next.reasons,
        at: this.chrono.serverNow(),
        atUncertainty: this.chrono.uncertainty(),
      });
    }
    this.state = next;
    return next;
  }

  private expectsCounterpart(): boolean {
    const m = this.inputs.mission();
    return counterpartExpected(this.inputs.role, m.state, m.seats);
  }
}
